/* eslint-disable no-param-reassign */

import { Action, Dispatch } from 'redux';
import { AxiosPromise } from 'axios';
import { IAppState } from '../reducers';
import { getVendorsWithTeamId, optInToVendor, optOutWithVendor } from '../../api/vault/vendors/api';
import { IVendor, IVendorUpdate } from '../../api/vault/vendors';
import { arrayCompare } from '../../utils/arrayCompare';
import { IApiResponse } from '../../api/vault';

export interface IState {
  isOpen: boolean;
  isLoading: boolean;
  vendors: IVendor[];
}

export enum ActionType {
  Toggle = 'VendorConsentSettingsAction.Toggle',
  StartLoading = 'VendorConsentSettingsAction.StartLoading',
  FinishLoading = 'VendorConsentSettingsAction.FinishLoading',
  UpdateVendors = 'VendorConsentSettingsAction.UpdateVendors',
  InitData = 'VendorConsentSettingsAction.InitData',
}

export interface IVendorSettingsAction extends Action<ActionType> {
  payload?: any,
}

const INITIAL_STATE: IState = {
  isOpen: false,
  isLoading: false,
  vendors: [],
};

export default function reducer(state: IState = INITIAL_STATE, action: IVendorSettingsAction): IState {
  switch (action.type) {
    case ActionType.Toggle:
      return {
        ...state,
        isOpen: !state.isOpen,
      };
    case ActionType.StartLoading:
      return {
        ...state,
        isLoading: true,
      };
    case ActionType.FinishLoading:
      return {
        ...state,
        isLoading: false,
      };
    case ActionType.InitData:
      return {
        ...state,
        vendors: action.payload.vendors,
      };
    case ActionType.UpdateVendors:
      return {
        ...state,
        vendors: action.payload,
      };
    default:
      return state;
  }
}

// Sorts vendors based on name and exchange qualifiers
const sortVendorData = (vendors: IVendor[]): IVendor[] => {
  const sorted = vendors.sort((a, b) => {
    if (a.name < b.name) { return -1; }
    if (a.name > b.name) { return 1; }
    return 0;
  });

  sorted.forEach((vendor) => {
    if (vendor.teams.length > 0 && vendor.teams[0].exchangeQualifier.length > 0) {
      vendor.teams[0].exchangeQualifier.sort((a, b) => vendor.availableQualifiers.indexOf(a) - vendor.availableQualifiers.indexOf(b));
    }
  });

  return sorted;
};

export const toggleVendorConsentSettings = () => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const {
    exchange: { vendorConsentSettings: { isOpen } },
  } = getState();
  dispatch({ type: ActionType.Toggle });

  // Get vendors
  if (!isOpen) {
    dispatch({ type: ActionType.StartLoading });
    const vendorsData = (await getVendorsWithTeamId()).data.data;
    vendorsData.forEach((vendor) => {
      if (vendor.teams[0] && vendor.teams[0].startGameDate) {
        const date = new Date(vendor.teams[0].startGameDate);
        date.setMinutes(date.getMinutes() + date.getTimezoneOffset());
        vendor.teams[0].startGameDate = date;
      }
    });
    dispatch({
      payload: {
        vendors: sortVendorData(vendorsData),
      },
      type: ActionType.InitData,
    });
    dispatch({ type: ActionType.FinishLoading });
  }
};

export const save = (currentVendors: IVendor[]) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  dispatch({ type: ActionType.StartLoading });
  const { exchange: { vendorConsentSettings: { vendors } } } = getState();

  const optInVendors: IVendor[] = [];
  const optOutVendors: IVendor[] = [];

  for (let i = 0; i < currentVendors.length; i += 1) {
    const currentVendor = currentVendors[i];
    const vendor = vendors[i];

    if (currentVendor.teams.length === 0) {
      // Opt out
      if (vendor.teams.length !== 0) {
        optOutVendors.push(currentVendor);
      }
    } else if (vendor.teams.length === 0) {
      // Team added new vendor
      optInVendors.push(currentVendor);
    } else if (!arrayCompare(vendor.teams[0].exchangeQualifier, currentVendor.teams[0].exchangeQualifier)) {
      // Different qualifiers selected
      optInVendors.push(currentVendor);
    } else if ((!currentVendor.teams[0].startGameDate && vendor.teams[0].startGameDate) || (currentVendor.teams[0].startGameDate && !vendor.teams[0].startGameDate)) {
      // Start date has been enabled/disabled
      optInVendors.push(currentVendor);
    } else if (vendor.teams[0].startGameDate && currentVendor.teams[0].startGameDate && (vendor.teams[0].startGameDate.getTime() !== currentVendor.teams[0].startGameDate.getTime())) {
      // Different start date selected
      optInVendors.push(currentVendor);
    }
  }

  if (optInVendors.length === 0 && optOutVendors.length === 0) {
    dispatch({ type: ActionType.FinishLoading });
    dispatch({ type: ActionType.Toggle });
    return;
  }

  const pendingUpdates: AxiosPromise<IApiResponse<IVendor[]>>[] = [];
  optInVendors.forEach((vendor) => {
    let dateString: string;
    const startDate = vendor.teams[0].startGameDate;
    if (startDate) dateString = `${startDate.getFullYear()}-${(`0${startDate.getMonth() + 1}`).slice(-2)}-${(`0${startDate.getDate()}`).slice(-2)}`; // Month starts at 0
    pendingUpdates.push(optInToVendor(<IVendorUpdate>{
      vendorOrgId: vendor.orgId,
      exchangeQualifiers: vendor.teams[0].exchangeQualifier,
      startGameDate: dateString,
    }));
  });
  optOutVendors.forEach((vendor) => {
    pendingUpdates.push(optOutWithVendor(<IVendorUpdate>{
      vendorOrgId: vendor.orgId,
    }));
  });

  const finishedUpdates = await Promise.all(pendingUpdates);
  dispatch({
    payload: sortVendorData(finishedUpdates[finishedUpdates.length - 1].data.data),
    type: ActionType.UpdateVendors,
  });
  dispatch({ type: ActionType.FinishLoading });
  dispatch({ type: ActionType.Toggle });
};
